import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreVertical, Edit, Trash2, Building2, Power, PowerOff } from 'lucide-react';
import { formatRut } from '../lib/utils';
import { REGIONES_CHILE, getComunasByRegion } from '../lib/chile';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Empresas = () => {
    const [loading, setLoading] = useState(true);
    const [empresas, setEmpresas] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingEmpresa, setEditingEmpresa] = useState(null);
    const [formData, setFormData] = useState({
        razon_social: '',
        rut: '',
        direccion: '',
        region: '',
        comuna: '',
        telefono: '',
        email: '',
        iva_porcentaje: '19.00',
        dias_mora_alerta: 30,
        nombre_legal: '',
        banco: '',
        cuenta_corriente: '',
        email_pagos: '',
    });

    useEffect(() => {
        fetchEmpresas();
    }, []);

    const fetchEmpresas = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/api/empresas`);
            setEmpresas(response.data);
        } catch (error) {
            console.error('Error fetching empresas:', error);
            toast.error('Error al cargar empresas');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (empresa = null) => {
        if (empresa) {
            setEditingEmpresa(empresa);
            setFormData({
                razon_social: empresa.razon_social,
                rut: formatRut(empresa.rut),
                direccion: empresa.direccion || '',
                region: empresa.region || '',
                comuna: empresa.comuna || '',
                telefono: empresa.telefono || '',
                email: empresa.email || '',
                iva_porcentaje: empresa.iva_porcentaje || '19.00',
                dias_mora_alerta: empresa.dias_mora_alerta || 30,
                nombre_legal: empresa.nombre_legal || '',
                banco: empresa.banco || '',
                cuenta_corriente: empresa.cuenta_corriente || '',
                email_pagos: empresa.email_pagos || '',
            });
        } else {
            setEditingEmpresa(null);
            setFormData({
                razon_social: '',
                rut: '',
                direccion: '',
                region: '',
                comuna: '',
                telefono: '',
                email: '',
                iva_porcentaje: '19.00',
                dias_mora_alerta: 30,
                nombre_legal: '',
                banco: '',
                cuenta_corriente: '',
                email_pagos: '',
            });
        }
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...formData,
                iva_porcentaje: parseFloat(formData.iva_porcentaje),
                dias_mora_alerta: parseInt(formData.dias_mora_alerta)
            };

            if (editingEmpresa) {
                await axios.put(`${API_URL}/api/empresas/${editingEmpresa.id}`, data);
                toast.success('Empresa actualizada');
            } else {
                await axios.post(`${API_URL}/api/empresas`, data);
                toast.success('Empresa creada');
            }
            setDialogOpen(false);
            fetchEmpresas();
        } catch (error) {
            console.error('Error saving empresa:', error);
            toast.error(error.response?.data?.detail || 'Error al guardar empresa');
        }
    };

    const handleToggleActive = async (empresa) => {
        try {
            await axios.put(`${API_URL}/api/empresas/${empresa.id}`, {
                activo: !empresa.activo
            });
            toast.success(`Empresa ${empresa.activo ? 'desactivada' : 'activada'}`);
            fetchEmpresas();
        } catch (error) {
            console.error('Error toggling empresa:', error);
            toast.error('Error al cambiar estado');
        }
    };

    const handleDelete = async (empresa) => {
        if (!window.confirm(`¿Está seguro de eliminar ${empresa.razon_social}? Esta acción no se puede deshacer.`)) return;
        
        try {
            await axios.delete(`${API_URL}/api/empresas/${empresa.id}`);
            toast.success('Empresa eliminada');
            fetchEmpresas();
        } catch (error) {
            console.error('Error deleting empresa:', error);
            toast.error('Error al eliminar empresa');
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Empresas</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Gestión de empresas acreedoras (Tenants)
                    </p>
                </div>
                <Button onClick={() => handleOpenDialog()} data-testid="new-empresa-btn">
                    <Plus size={18} className="mr-2" />
                    Nueva Empresa
                </Button>
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B9A9A]"></div>
                        </div>
                    ) : empresas.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                            <Building2 size={48} className="text-slate-300 mb-2" />
                            <p>No hay empresas registradas</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header border-b">
                                        <th className="text-left py-3 px-4">Empresa</th>
                                        <th className="text-left py-3 px-4">RUT</th>
                                        <th className="text-left py-3 px-4 hidden md:table-cell">Email</th>
                                        <th className="text-center py-3 px-4">IVA</th>
                                        <th className="text-center py-3 px-4">Estado</th>
                                        <th className="text-right py-3 px-4">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {empresas.map((empresa) => (
                                        <tr key={empresa.id} className="table-row">
                                            <td className="table-cell">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center">
                                                        <Building2 className="text-slate-600" size={18} />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{empresa.razon_social}</p>
                                                        {empresa.telefono && (
                                                            <p className="text-xs text-slate-500">{empresa.telefono}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="table-cell text-slate-600">{empresa.rut}</td>
                                            <td className="table-cell hidden md:table-cell text-slate-600">
                                                {empresa.email || '-'}
                                            </td>
                                            <td className="table-cell text-center">
                                                {empresa.iva_porcentaje}%
                                            </td>
                                            <td className="table-cell text-center">
                                                <Badge className={empresa.activo ? 'badge-success' : 'badge-error'}>
                                                    {empresa.activo ? 'Activa' : 'Inactiva'}
                                                </Badge>
                                            </td>
                                            <td className="table-cell text-right">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon">
                                                            <MoreVertical size={16} />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => handleOpenDialog(empresa)}>
                                                            <Edit size={16} className="mr-2" />
                                                            Editar
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleToggleActive(empresa)}>
                                                            {empresa.activo ? (
                                                                <>
                                                                    <PowerOff size={16} className="mr-2" />
                                                                    Desactivar
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Power size={16} className="mr-2" />
                                                                    Activar
                                                                </>
                                                            )}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem 
                                                            onClick={() => handleDelete(empresa)}
                                                            className="text-red-600"
                                                        >
                                                            <Trash2 size={16} className="mr-2" />
                                                            Eliminar
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="razon_social">Razón Social *</Label>
                                <Input
                                    id="razon_social"
                                    value={formData.razon_social}
                                    onChange={(e) => setFormData({...formData, razon_social: e.target.value})}
                                    required
                                    data-testid="input-empresa-razon"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="rut">RUT *</Label>
                                <Input
                                    id="rut"
                                    value={formData.rut}
                                    onChange={(e) => setFormData({ ...formData, rut: formatRut(e.target.value) })}
                                    required
                                    placeholder="76.123.456-7"
                                    maxLength={12}
                                    data-testid="input-empresa-rut"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="direccion">Dirección</Label>
                                <Input
                                    id="direccion"
                                    value={formData.direccion}
                                    onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                                    placeholder="Calle, número, oficina"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="region">Región</Label>
                                    <select
                                        id="region"
                                        value={formData.region}
                                        onChange={(e) => setFormData({ ...formData, region: e.target.value, comuna: '' })}
                                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3D7A7A]/30"
                                        data-testid="select-empresa-region"
                                    >
                                        <option value="">Selecciona una región…</option>
                                        {REGIONES_CHILE.map((r) => (
                                            <option key={r.nombre} value={r.nombre}>{r.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="comuna">Comuna</Label>
                                    <select
                                        id="comuna"
                                        value={formData.comuna}
                                        onChange={(e) => setFormData({ ...formData, comuna: e.target.value })}
                                        disabled={!formData.region}
                                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3D7A7A]/30 disabled:bg-slate-50 disabled:text-slate-400"
                                        data-testid="select-empresa-comuna"
                                    >
                                        <option value="">{formData.region ? 'Selecciona una comuna…' : 'Primero elige una región'}</option>
                                        {getComunasByRegion(formData.region).map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="telefono">Teléfono</Label>
                                    <Input
                                        id="telefono"
                                        value={formData.telefono}
                                        onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="iva_porcentaje">IVA (%)</Label>
                                    <Input
                                        id="iva_porcentaje"
                                        type="number"
                                        step="0.01"
                                        value={formData.iva_porcentaje}
                                        onChange={(e) => setFormData({...formData, iva_porcentaje: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="dias_mora_alerta">Días alerta mora</Label>
                                    <Input
                                        id="dias_mora_alerta"
                                        type="number"
                                        value={formData.dias_mora_alerta}
                                        onChange={(e) => setFormData({...formData, dias_mora_alerta: parseInt(e.target.value)})}
                                    />
                                </div>
                            </div>

                            {/* Datos bancarios para EDP */}
                            <div className="border-t pt-4 mt-4">
                                <p className="text-sm font-semibold text-slate-700 mb-3">Datos Bancarios (para EDP)</p>
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <Label htmlFor="nombre_legal">Nombre Legal Completo</Label>
                                        <Input
                                            id="nombre_legal"
                                            value={formData.nombre_legal}
                                            onChange={(e) => setFormData({...formData, nombre_legal: e.target.value})}
                                            placeholder="Ej: LEGAV SERVICIOS INTEGRALES LIMITADA"
                                            data-testid="input-empresa-nombre-legal"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="banco">Banco</Label>
                                            <Input
                                                id="banco"
                                                value={formData.banco}
                                                onChange={(e) => setFormData({...formData, banco: e.target.value})}
                                                placeholder="Ej: Banco Estado"
                                                data-testid="input-empresa-banco"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="cuenta_corriente">N° Cuenta Corriente</Label>
                                            <Input
                                                id="cuenta_corriente"
                                                value={formData.cuenta_corriente}
                                                onChange={(e) => setFormData({...formData, cuenta_corriente: e.target.value})}
                                                data-testid="input-empresa-cuenta"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email_pagos">Email para Pagos</Label>
                                        <Input
                                            id="email_pagos"
                                            type="email"
                                            value={formData.email_pagos}
                                            onChange={(e) => setFormData({...formData, email_pagos: e.target.value})}
                                            placeholder="pagos@empresa.cl"
                                            data-testid="input-empresa-email-pagos"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" data-testid="save-empresa-btn">
                                {editingEmpresa ? 'Guardar cambios' : 'Crear empresa'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Empresas;
